import { type TranslateOptions } from './interpolation.helpers';
import { type WordingTranslation } from './wording.types';

export function setWordingWithDots(
  keyPath: string[] | string,
  value: string | WordingTranslation,
  object: any,
): string[] {
  if (!keyPath.length) {
    throw new Error(
      "Developer error: can't have empty keyPath in setPropertyWithArray",
    );
  }

  const resultKeys = [];
  const keys = typeof keyPath === 'string' ? keyPath.split('.') : keyPath;

  const currentStack = [object];

  for (let i = 0; i < keys.length; i++) {
    let key = keys[i];
    const current = currentStack[currentStack.length - 1];

    while (
      current[key] &&
      typeof current[key] === 'string' &&
      i < keys.length - 1
    ) {
      i++;
      key = `${key}.${keys[i]}`;
    }

    if (i === keys.length - 1) {
      resultKeys.push(key);

      if (current[key] && typeof current[key] === 'object') {
        if (typeof value === 'object') {
          Object.assign(current[key], value);

          return resultKeys;
        }

        for (const [objKey, objValue] of Object.entries(current[key])) {
          current[`${key}.${objKey}`] = objValue;
        }
      }

      current[key] = value;

      return resultKeys;
    }

    current[key] ||= {};
    currentStack.push(current[key]);
    resultKeys.push(key);
  }
}

export function getWordingWithDots<
  T extends string | ((options: TranslateOptions) => string),
>(keyPath: string[] | string, object: any = {}, defaultValue?: any): T {
  if (!keyPath || !object || typeof object !== 'object') {
    return defaultValue;
  }

  const keys = typeof keyPath === 'string' ? keyPath.split('.') : keyPath;
  let remainingKey = '';

  for (let i = 0; i < keys.length; i++) {
    const key = `${remainingKey ? `${remainingKey}.` : ''}${keys[i]}`;

    if (i === keys.length - 1) {
      if (
        typeof object[key] === 'string' ||
        typeof object[key] === 'function'
      ) {
        return object[key] || defaultValue;
      }

      return defaultValue;
    }

    // If it doesn't exist, we consider the dot is part of the key, not a property separator
    if (!object[key] || typeof object[key] === 'string') {
      remainingKey = key;
    } else {
      object = object[key];
      remainingKey = '';
    }
  }

  return defaultValue;
}

export function preventFunctionStringification<
  T extends (...args: any[]) => any,
>(fnc: T): void {
  fnc.toString = () => {
    throw new Error(
      'You are trying to stringify a function that should not be stringified',
    );
  };
}

export function patchI18nToPreventFunctionStringification(i18n: any): void {
  for (const value of Object.values(i18n)) {
    if (typeof value === 'object') {
      patchI18nToPreventFunctionStringification(value);
    } else if (typeof value === 'function') {
      preventFunctionStringification(value as (...args: any[]) => any);
    }
  }
}
